import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, Clock3, CheckCircle2, CalendarClock, TrendingUp, MessageSquare, ClipboardList, KeyRound, Copy, FolderOpen, BookOpen, Plug, Archive, ArchiveRestore, Trash2, ShieldAlert, Activity, Users2, UserPlus, Radio, Pencil } from "lucide-react";
import { StatusPill } from "../components/StatusPill";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatMoney } from "../lib/currency";
import { servicesOf, hasSocial, hasDev, PM_SLOT, teamPlan, canSeeCosts } from "../lib/services";
import ClientForm from "../components/ClientForm";

const SMM_SLOTS = [
  { field: "assigned_account_manager", role: "account_manager", label: "Account Manager" },
  { field: "assigned_graphic_designer", role: "graphic_designer", label: "Graphic Designer" },
  { field: "assigned_content_writer", role: "content_writer", label: "Content Writer" },
  { field: "assigned_execution_specialist", role: "execution_specialist", label: "Execution Specialist" },
];
const ADDITIONAL_ROLES = new Set(["project_manager", "developer", "tester", "sales_head", "sales_member", "ceo"]);
const ROLE_LABEL = {
  account_manager: "Account Manager", graphic_designer: "Graphic Designer",
  content_writer: "Content Writer", execution_specialist: "Execution Specialist",
  project_manager: "Project Manager", developer: "Developer", tester: "Tester",
  ceo: "CEO", sales_head: "Sales Head", sales_member: "Sales Member",
};

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [portalUser, setPortalUser] = useState(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [liveStats, setLiveStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadError, setLoadError] = useState("");

  const isAdmin = ["admin", "ceo", "coo"].includes(user?.role);

  const load = async () => {
    try {
      const [d, u, p, s] = await Promise.all([
        api.get(`/clients/${id}/dashboard`),
        api.get(`/users`).catch(()=>({ data: [] })),
        isAdmin ? api.get(`/clients/${id}/portal-user`).catch(()=>({ data: null })) : Promise.resolve({ data: null }),
        api.get(`/clients/${id}/live-stats`).catch(()=>({ data: null })),
      ]);
      setData(d.data); setUsers(u.data); setPortalUser(p.data); setLiveStats(s.data);
      const pr = await api.get("/projects").catch(()=>({ data: [] }));
      setProjects(pr.data.filter(x => x.client_id === id));
    } catch (e) {
      setLoadError(formatApiError(e.response?.data?.detail) || "Could not load this client.");
    }
  };
  useEffect(()=>{ setLoadError(""); load(); }, [id]);

  if (loadError) return (
    <div className="p-8 space-y-3" data-testid="client-load-error">
      <Link to="/clients" className="inline-flex items-center gap-2 text-xs font-mono uppercase text-neutral-600 hover:text-neutral-800"><ArrowLeft className="w-3 h-3"/> All Clients</Link>
      <div className="panel p-6 text-sm text-rose-700">{loadError}</div>
    </div>
  );
  if (!data) return <div className="p-8 text-neutral-500">Loading…</div>;
  const { client, contents, stats } = data;
  const nameOf = (uid) => users.find(u=>u.id===uid)?.name || "Unassigned";
  const isArchived = client.status === "Archived";
  const social = hasSocial(client);
  const dev = (hasDev(client) || projects.length > 0) && !["sales_head", "sales_member"].includes(user?.role);
  const plan = teamPlan(servicesOf(client));

  const archive = async () => {
    try {
      await api.post(`/clients/${id}/archive`);
      toast.success(`${client.client_name} archived`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unarchive = async () => {
    try {
      await api.post(`/clients/${id}/unarchive`);
      toast.success(`${client.client_name} restored`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      navigate("/clients");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <Link to={`/clients`} className="inline-flex items-center gap-2 text-xs font-mono uppercase text-neutral-600 hover:text-neutral-800"><ArrowLeft className="w-3 h-3"/> All Clients</Link>

      <div className="flex items-center justify-end -mt-4 gap-2 flex-wrap">
        <Link to={`/clients/${id}/timeline`} data-testid="open-timeline-btn">
          <Button variant="outline" size="sm" className="border-[#E4E7EC]">
            <Activity className="w-4 h-4 mr-1"/> Timeline
          </Button>
        </Link>
        {social && (
          <Link to={`/clients/${id}/connections`} data-testid="open-connections-btn">
            <Button variant="outline" size="sm" className="border-[#E4E7EC]">
              <Plug className="w-4 h-4 mr-1"/> Social Connections
            </Button>
          </Link>
        )}
        {social && (
          <Link to={`/clients/${id}/strategy`} data-testid="open-strategy-btn">
            <Button variant="outline" size="sm" className="border-[#E4E7EC]">
              <BookOpen className="w-4 h-4 mr-1"/> Strategy
            </Button>
          </Link>
        )}
        <Link to={`/clients/${id}/library`} data-testid="open-library-btn">
          <Button variant="outline" size="sm" className="border-[#E4E7EC]">
            <FolderOpen className="w-4 h-4 mr-1"/> Content Library
          </Button>
        </Link>
        {isAdmin && (
          <>
            <Button variant="outline" size="sm" className="border-[#E4E7EC]" onClick={()=>setEditOpen(true)} data-testid="edit-client-btn">
              <Pencil className="w-4 h-4 mr-1"/> Edit Client
            </Button>
            <Button variant="outline" size="sm" className="border-[#E4E7EC]" onClick={()=>setRosterOpen(true)} data-testid="assign-team-roster-btn">
              <UserPlus className="w-4 h-4 mr-1"/> Assign Team Roster
            </Button>
            {isArchived ? (
              <Button variant="outline" size="sm" className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10" onClick={unarchive} data-testid="unarchive-client-btn">
                <ArchiveRestore className="w-4 h-4 mr-1"/> Restore
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="border-yellow-500/40 text-yellow-700 hover:bg-yellow-500/10" onClick={archive} data-testid="archive-client-btn">
                <Archive className="w-4 h-4 mr-1"/> Archive
              </Button>
            )}
            <Button variant="outline" size="sm" className="border-rose-500/40 text-rose-700 hover:bg-rose-500/10" onClick={()=>setConfirmDelete(true)} data-testid="delete-client-btn">
              <Trash2 className="w-4 h-4 mr-1"/> Delete
            </Button>
          </>
        )}
      </div>

      {/* Banner */}
      <div className="panel p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-500/10 blur-3xl"/>
        <div className="absolute inset-0 grid-lines opacity-20"/>
        <div className="relative flex items-center gap-6 flex-wrap">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500/40 to-red-500/30 border border-orange-500/30 grid place-items-center text-2xl font-extrabold">
            {(client.brand_name || client.client_name || "?").slice(0,2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">{client.industry}</div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="client-detail-name">{client.client_name}</h1>
            <div className="text-neutral-600 text-sm mt-1" data-testid="client-contact-line">{[client.contact_person, client.email, client.phone].filter(Boolean).join(" · ") || "No contact details added"}</div>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="client-services">
              {servicesOf(client).map(s => <span key={s} className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-700">{s}</span>)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusPill status={client.status}/>
            {canSeeCosts(user) && <>
              <div className="text-2xl font-bold text-emerald-700" data-testid="client-monthly-retainer">{formatMoney(client.monthly_retainer, client.currency)}<span className="text-xs text-neutral-500 font-mono">/mo</span></div>
              {client.one_time_total > 0 && <div className="text-xs font-semibold text-neutral-700" data-testid="client-onetime-total">+ {formatMoney(client.one_time_total, client.currency)} one-time</div>}
            </>}
            <div className="text-[10px] font-mono text-neutral-500 uppercase">{client.package} Package</div>
            {isAdmin && (portalUser ? (
              <div className="flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-700" data-testid="portal-user-badge">
                <KeyRound className="w-3 h-3"/> Portal: {portalUser.email}
              </div>
            ) : (
              <Button size="sm" variant="outline" className="border-orange-500/40 text-orange-700 hover:bg-orange-500/10" onClick={()=>setPortalOpen(true)} data-testid="create-portal-login-btn">
                <KeyRound className="w-3.5 h-3.5 mr-1"/> Create Portal Login
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Widgets */}
      <div className="grid lg:grid-cols-12 gap-4">
        {social && <>
        <StatWidget cls="lg:col-span-3" icon={<Clock3 className="w-4 h-4"/>} label="Drafts" value={stats.pending.length} tint="draft" testid="stat-drafts"/>
        <StatWidget cls="lg:col-span-3" icon={<Clock3 className="w-4 h-4"/>} label="Pending Approval" value={stats.pending_approvals.length} tint="pending" testid="stat-pending-approval"/>
        <StatWidget cls="lg:col-span-3" icon={<CalendarClock className="w-4 h-4"/>} label="Scheduled" value={stats.upcoming.length} tint="scheduled"/>
        <StatWidget cls="lg:col-span-3" icon={<CheckCircle2 className="w-4 h-4"/>} label="Published" value={stats.published.length} tint="published"/>

        <section className="panel p-6 lg:col-span-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-lg tracking-tight">This Month's Content</h3>
              <div className="text-[11px] font-mono text-neutral-500 uppercase">{stats.current_month.length} pieces</div>
            </div>
            <Link to={`/calendar?client=${client.id}`} className="text-xs font-mono text-orange-400 uppercase tracking-wider">Open Calendar →</Link>
          </div>
          <div className="space-y-2 max-h-96 overflow-auto">
            {stats.current_month.length===0 && <div className="text-sm text-neutral-500">No content this month yet.</div>}
            {stats.current_month.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2 px-3 rounded-lg border border-transparent hover:border-[#E4E7EC] hover:bg-white/[0.02]" data-testid={`content-row-${c.id}`}>
                <div className="text-[10px] font-mono text-neutral-500 w-16">{c.publishing_date?.slice(5)} {c.publishing_time}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.content_topic || c.content_type}</div>
                  <div className="text-[10px] font-mono text-neutral-500 uppercase">{c.platform} · {c.content_type} · {c.content_pillar}</div>
                </div>
                <StatusPill status={c.status}/>
              </div>
            ))}
          </div>
        </section>
        </>}

        {dev && (
          <section className={`panel p-6 ${social ? "lg:col-span-12 order-last" : "lg:col-span-8"}`} data-testid="client-projects-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg tracking-tight">Development Projects</h3>
              <Link to="/projects" className="text-xs font-mono text-orange-600 uppercase tracking-wider">All Projects →</Link>
            </div>
            {projects.length === 0 && <div className="text-sm text-neutral-500">No projects for this client yet.</div>}
            <div className="space-y-2">
              {projects.map(p => (
                <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center gap-3 py-2 px-3 rounded-lg border border-[#E4E7EC] hover:border-orange-400" data-testid={`client-project-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.project_name}</div>
                    <div className="text-[10px] font-mono text-neutral-500 uppercase">{p.project_type || "—"} · PM {nameOf(p.project_manager_id)} · Due {p.expected_completion_date || "—"}</div>
                  </div>
                  <StatusPill status={p.status}/>
                </Link>
              ))}
            </div>
          </section>
        )}

        <aside className="panel p-6 lg:col-span-4 space-y-4 min-w-0 overflow-hidden">
          <h3 className="font-bold text-lg tracking-tight">Client Information</h3>
          <InfoRow label="Brand" value={client.brand_name}/>
          {client.website && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Website</div>
              <a href={client.website.startsWith("http") ? client.website : `https://${client.website}`} target="_blank" rel="noreferrer" className="text-sm text-orange-700 hover:underline break-all" data-testid="client-website-link">{client.website}</a>
            </div>
          )}
          {(client.contract_start || client.contract_end) && <InfoRow label="Contract" value={`${client.contract_start || "—"} → ${client.contract_end || "—"}`}/>}
          <div className="pt-2 border-t border-[#E4E7EC] text-[11px] font-mono text-neutral-500 uppercase">Assigned Team</div>
          <div className="grid grid-cols-2 gap-2 text-[12px]" data-testid="client-assigned-team">
            {plan.slots.map(s => (
              <div key={s.field} data-testid={`ct-${s.role}`}><span className="text-neutral-500">{s.label}</span> <span className="text-neutral-800">{nameOf(client[s.field])}</span></div>
            ))}
            {dev && <div data-testid="ct-project_manager"><span className="text-neutral-500">Project Manager</span> <span className="text-neutral-800">{nameOf(client.assigned_project_manager)}</span></div>}
            {dev && <div className="col-span-2" data-testid="ct-developers"><span className="text-neutral-500">Developers</span> <span className="text-neutral-800">{(client.assigned_developers || []).map(nameOf).join(", ") || "Unassigned"}</span></div>}
          </div>
          {social && <>
          <div className="pt-2 border-t border-[#E4E7EC] text-[11px] font-mono text-neutral-500 uppercase">Social Handles</div>
          <div className="space-y-1.5 text-[12px] min-w-0" data-testid="client-social-handles">
            {[["IG", client.instagram, "text-pink-700"], ["FB", client.facebook, "text-blue-700"], ["LI", client.linkedin, "text-sky-700"], ["YT", client.youtube, "text-red-700"]].filter(([, v]) => v).map(([k, v, cls]) => (
              <div key={k} className="flex items-start gap-2 min-w-0">
                <span className="text-neutral-500 w-6 shrink-0">{k}</span>
                <span className={`${cls} font-mono break-all min-w-0`}>{v}</span>
              </div>
            ))}
            {![client.instagram, client.facebook, client.linkedin, client.youtube].some(Boolean) && <div className="text-neutral-500">No social handles added.</div>}
          </div>
          </>}
        </aside>

        {social && <>
        <section className="panel p-6 lg:col-span-6">
          <h3 className="font-bold text-lg tracking-tight mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-amber-400"/> Pending Approvals</h3>
          <div className="space-y-2">
            {stats.pending_approvals.length===0 && <div className="text-sm text-neutral-500">All caught up. Nothing awaiting approval.</div>}
            {stats.pending_approvals.slice(0,6).map(c=> (
              <div key={c.id} className="flex items-center justify-between py-2 border-b border-[#E4E7EC] last:border-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.content_topic || c.content_type}</div>
                  <div className="text-[10px] font-mono text-neutral-500">{c.platform} · {c.publishing_date}</div>
                </div>
                <StatusPill status={c.status}/>
              </div>
            ))}
          </div>
        </section>

        <section className="panel p-6 lg:col-span-6">
          <h3 className="font-bold text-lg tracking-tight mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400"/> Performance Snapshot</h3>
          <div className="grid grid-cols-2 gap-3">
            {liveStats?.is_live ? (
              <>
                <PerfBox label="Reach (28d)" value={fmtNum(liveStats.metrics.reach)} delta={liveStats.metrics.reach ? "live" : "—"}/>
                <PerfBox label="Impressions" value={fmtNum(liveStats.metrics.impressions)} delta={liveStats.metrics.impressions ? "live" : "—"}/>
                <PerfBox label="Engagement" value={fmtNum(liveStats.metrics.engagement)} delta={liveStats.metrics.engagement ? "live" : "—"}/>
                <PerfBox label="Followers" value={fmtNum(liveStats.metrics.followers)} delta={liveStats.metrics.followers ? "live" : "—"}/>
              </>
            ) : (
              <>
                <PerfBox label="Reach" value={`${(stats.published.length*4.2).toFixed(1)}K`} delta="+12%"/>
                <PerfBox label="Engagement" value={`${(stats.published.length*3.1).toFixed(1)}%`} delta="+2.4%"/>
                <PerfBox label="New Followers" value={`${stats.published.length*23}`} delta="+8%"/>
                <PerfBox label="Post CTR" value={`${(stats.published.length*0.6).toFixed(2)}%`} delta="+0.3%"/>
              </>
            )}
          </div>
          <div className="mt-3 text-[10px] font-mono text-neutral-500 uppercase flex items-center gap-1.5" data-testid="perf-source">
            {liveStats?.is_live ? (
              <>
                <Radio className="w-3 h-3 text-emerald-600"/> <span className="text-emerald-700">Live from {liveStats.sources.map(s=>s.provider==='meta'?'Meta':'LinkedIn').join(' + ')}</span>
              </>
            ) : (
              <>Simulated aggregates — <Link to={`/clients/${id}/connections`} className="text-orange-600 hover:underline">connect socials</Link> for live numbers</>
            )}
          </div>
        </section>
        </>}

        <PortalLoginDialog open={portalOpen} onOpenChange={setPortalOpen} clientId={id} clientContact={client.contact_person} clientEmail={client.email} onSaved={load}/>
        <TeamRosterDialog open={rosterOpen} onOpenChange={setRosterOpen} client={client} users={users} onSaved={load}/>
        <ClientForm open={editOpen} onOpenChange={setEditOpen} initial={client} onSaved={load}/>
        {social && <section className="panel p-6 lg:col-span-12">
          <h3 className="font-bold text-lg tracking-tight mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-red-400"/> Client Communication</h3>
          <div className="space-y-2 text-sm">
            <MessageItem who={client.contact_person} role="Client" text="Loving this month's carousel drops! Can we push the reel to Friday?"/>
            <MessageItem who="Meera Iyer" role="Account Manager" text="Absolutely — we'll ship revised captions by EOD."/>
            <MessageItem who="Priya Nair" role="Copywriter" text="Uploading V2 to approval queue in 30 mins."/>
          </div>
        </section>}
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-md" data-testid="delete-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-rose-700"><ShieldAlert className="w-5 h-5"/> Delete client permanently?</DialogTitle>
            <DialogDescription className="text-neutral-600 text-xs">
              Deleting <strong className="text-neutral-800">{client.client_name}</strong> will also erase every post, library upload, strategy version, portal login, and open request tied to them.
            </DialogDescription>
          </DialogHeader>
          <div className="text-[11px] font-mono text-yellow-700 bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mt-2">
            Consider Archive first — same effect but reversible.
          </div>
          <div className="text-[11px] font-mono text-rose-700 bg-rose-500/10 border border-rose-500/30 rounded p-2">This action cannot be undone.</div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setConfirmDelete(false)} data-testid="delete-client-cancel">Cancel</Button>
            <Button onClick={remove} className="bg-rose-500 hover:bg-rose-600 text-neutral-900" data-testid="delete-client-confirm">Delete Client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({label, value}) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}
function StatWidget({cls, icon, label, value, tint, testid}) {
  const cls2 = {
    draft: "text-neutral-700 border-neutral-500/30 bg-neutral-500/5",
    pending: "text-amber-700 border-amber-500/30 bg-amber-500/5",
    scheduled: "text-blue-700 border-blue-500/30 bg-blue-500/5",
    published: "text-red-700 border-red-500/30 bg-red-500/5",
  }[tint];
  return (
    <div className={`panel p-5 ${cls}`} data-testid={testid}>
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border font-mono text-[10px] uppercase tracking-wider ${cls2}`}>{icon}{label}</div>
      <div className="mt-3 text-3xl font-extrabold tracking-tight">{value}</div>
    </div>
  );
}
function PerfBox({label, value, delta}) {
  return (
    <div className="rounded-lg border border-[#E4E7EC] p-3 bg-[#FFFFFF]">
      <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
      <div className={`text-[11px] font-mono ${delta === "live" ? "text-emerald-600" : delta === "—" ? "text-neutral-400" : "text-emerald-400"}`}>{delta}</div>
    </div>
  );
}

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n/1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n/1_000).toFixed(1) + "K";
  return String(n);
}

function TeamRosterDialog({ open, onOpenChange, client, users, onSaved }) {
  const [form, setForm] = useState({
    assigned_account_manager: "",
    assigned_graphic_designer: "",
    assigned_content_writer: "",
    assigned_execution_specialist: "",
    assigned_project_manager: "",
    assigned_developers: [],
    additional_user_ids: [],
    notify_new_members: true,
  });
  const [busy, setBusy] = useState(false);
  const plan = teamPlan(servicesOf(client));
  const slots = [...plan.slots, ...(plan.dev ? [PM_SLOT] : [])];

  useEffect(()=>{
    if (open && client) {
      setForm({
        assigned_account_manager: client.assigned_account_manager || "",
        assigned_graphic_designer: client.assigned_graphic_designer || "",
        assigned_content_writer: client.assigned_content_writer || "",
        assigned_execution_specialist: client.assigned_execution_specialist || "",
        assigned_project_manager: client.assigned_project_manager || "",
        assigned_developers: client.assigned_developers || [],
        additional_user_ids: client.assigned_team || [],
        notify_new_members: true,
      });
    }
  }, [open, client]);

  const usersByRole = useMemo(() => {
    const map = {};
    (users || []).filter(u => !u.archived_at).forEach(u => {
      (map[u.role] = map[u.role] || []).push(u);
    });
    return map;
  }, [users]);

  const additionalCandidates = useMemo(() =>
    (users || []).filter(u => !u.archived_at && ADDITIONAL_ROLES.has(u.role) && !(plan.dev && u.role === "developer")),
  [users, plan.dev]);

  const toggleExtra = (uid, key = "additional_user_ids") => setForm(f => ({
    ...f,
    [key]: f[key].includes(uid)
      ? f[key].filter(x => x !== uid)
      : [...f[key], uid],
  }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/team-roster`, form);
      if (data.added > 0 && form.notify_new_members) {
        toast.success(`Roster saved · ${data.added} new member${data.added===1?'':'s'} added · ${data.emailed} email${data.emailed===1?'':'s'} sent`);
      } else if (data.added > 0) {
        toast.success(`Roster saved · ${data.added} new member${data.added===1?'':'s'} added`);
      } else {
        toast.success("Roster saved");
      }
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="team-roster-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2"><Users2 className="w-5 h-5 text-orange-500"/> Assign Team Roster</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">
            Pick the whole team for {client?.client_name} in one shot. Newly assigned members get a heads-up email so they know it's their new account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {slots.map(slot => {
              const options = usersByRole[slot.role] || [];
              return (
                <div key={slot.field} className="space-y-1.5">
                  <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">{slot.label}</Label>
                  <Select value={form[slot.field] || "__none__"} onValueChange={(v)=>setForm({...form, [slot.field]: v === "__none__" ? "" : v})}>
                    <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid={`roster-${slot.role}`}><SelectValue placeholder={`Pick ${slot.label}`}/></SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                      <SelectItem value="__none__">— Unassigned —</SelectItem>
                      {options.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      {options.length === 0 && <div className="px-3 py-2 text-[11px] text-neutral-500">No members with this role yet</div>}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>

          {plan.dev && (
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Developers ({form.assigned_developers.length})</Label>
              <div className="mt-2 rounded-lg border border-[#E4E7EC] max-h-44 overflow-y-auto divide-y divide-[#E4E7EC]" data-testid="roster-developers">
                {(usersByRole.developer || []).length === 0 && <div className="px-3 py-2 text-[11px] text-neutral-500">No developers in the team yet</div>}
                {(usersByRole.developer || []).map(u => (
                  <label key={u.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-neutral-50" data-testid={`roster-dev-${u.id}`}>
                    <Checkbox checked={form.assigned_developers.includes(u.id)} onCheckedChange={()=>toggleExtra(u.id, "assigned_developers")}/>
                    <span className="text-sm">{u.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {additionalCandidates.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Additional team members ({form.additional_user_ids.length})</Label>
              <div className="mt-2 rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] max-h-52 overflow-y-auto divide-y divide-[#E4E7EC]">
                {additionalCandidates.map(u => (
                  <label key={u.id} className="flex items-center gap-3 p-3 hover:bg-white/[0.02] cursor-pointer" data-testid={`roster-additional-${u.id}`}>
                    <Checkbox checked={form.additional_user_ids.includes(u.id)} onCheckedChange={()=>toggleExtra(u.id)}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{u.name}</div>
                      <div className="text-[10px] font-mono text-neutral-500 uppercase">{ROLE_LABEL[u.role] || u.role} · {u.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-[#E4E7EC] bg-[#F5F7FB] hover:border-[#CBD1DC]" data-testid="roster-notify-toggle">
            <Checkbox checked={form.notify_new_members} onCheckedChange={(v)=>setForm({...form, notify_new_members: !!v})} className="mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Email each newly assigned member</div>
              <div className="text-[11px] text-neutral-600">Existing members (already on this account) are never re-emailed. Only new additions receive a heads-up.</div>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="roster-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="roster-save">
            {busy ? "Saving..." : "Save Roster"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function MessageItem({who, role, text}) {
  return (
    <div className="flex gap-3 py-2 border-b border-[#E4E7EC] last:border-0">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500/40 to-orange-500/30 grid place-items-center text-[10px] font-bold shrink-0">{who.split(" ").map(x=>x[0]).slice(0,2).join("")}</div>
      <div>
        <div className="text-xs"><span className="font-semibold">{who}</span> <span className="text-neutral-500 font-mono">· {role}</span></div>
        <div className="text-sm text-neutral-700">{text}</div>
      </div>
    </div>
  );
}

function PortalLoginDialog({ open, onOpenChange, clientId, clientContact, clientEmail, onSaved }) {
  const [form, setForm] = useState({ name:"", email:"", password:"Client@12345" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  useEffect(()=>{
    if (open) {
      setForm({ name: clientContact || "", email: clientEmail || "", password: "Client@12345" });
      setCreated(null);
    }
  }, [open, clientContact, clientEmail]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/portal-user`, form);
      setCreated({ email: form.email, password: form.password });
      toast.success("Portal login created");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copyCreds = () => {
    navigator.clipboard.writeText(`Email: ${created.email}\nPassword: ${created.password}`);
    toast.success("Copied");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-md" data-testid="portal-login-dialog">
        <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><KeyRound className="w-5 h-5 text-orange-400"/>Create Portal Login</DialogTitle></DialogHeader>
        {!created ? (
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Client Contact Name</Label>
              <Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="pl-name" className="bg-[#FFFFFF] border-[#E4E7EC] mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Email</Label>
              <Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="pl-email" className="bg-[#FFFFFF] border-[#E4E7EC] mt-1"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Temporary Password</Label>
              <Input value={form.password} onChange={e=>setForm({...form, password:e.target.value})} data-testid="pl-password" className="bg-[#FFFFFF] border-[#E4E7EC] mt-1"/>
            </div>
            <div className="text-[11px] font-mono text-neutral-500 pt-2 border-t border-[#E4E7EC]">Client will use this to review approvals, calendar, live stats and raise creative requests.</div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4">
              <div className="text-emerald-700 text-sm font-semibold">Login created — share these credentials</div>
              <div className="mt-2 font-mono text-sm">
                <div><span className="text-neutral-500">Email:</span> {created.email}</div>
                <div><span className="text-neutral-500">Password:</span> {created.password}</div>
              </div>
            </div>
            <Button variant="outline" onClick={copyCreds} data-testid="pl-copy" className="w-full border-[#E4E7EC]"><Copy className="w-4 h-4 mr-1"/>Copy credentials</Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="pl-close">Close</Button>
          {!created && <Button onClick={save} disabled={busy || !form.email || !form.name} data-testid="pl-save" className="bg-orange-500 hover:bg-orange-600 text-white">{busy?"Creating...":"Create"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
